import { useState } from 'react';
import { useLocalStorageValue } from '@react-hookz/web';
import {
  Sheet,
  SheetContentResizable,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  useArPayment,
  useSetArPaymentStatus,
} from '@/api/rpc/hooks/accounts-receivable/useArPayments';
import {
  OriginReadOnlyBadge,
  isManualOrigin,
} from '@/components/common/OriginReadOnlyBadge';
import { useToast } from 'src/context/ToastContext';
import { ARPaymentDetails } from './ar-payment-details';
import { PaymentFormDialog } from './PaymentFormDialog';
import { T, useGT } from 'gt-react';

function getStatusBadge(status: string, gt: ReturnType<typeof useGT>) {
  if (status === 'pending')
    return (
      <Badge variant="secondary">
        <T context="AR payment status badge, pending">Pending</T>
      </Badge>
    );
  if (status === 'cleared') {
    return (
      <Badge
        variant="outline"
        className="bg-green-50 text-green-800 border-green-200"
      >
        <T context="AR payment status badge, cleared">Cleared</T>
      </Badge>
    );
  }
  if (status === 'reversed') {
    return (
      <Badge className="bg-amber-100 text-amber-800">
        <T context="AR payment status badge, reversed">Reversed</T>
      </Badge>
    );
  }
  if (status === 'voided') {
    return (
      <Badge variant="outline" className="text-muted-foreground">
        <T context="AR payment status badge, voided">Voided</T>
      </Badge>
    );
  }
  return <Badge variant="outline">{status}</Badge>;
}

interface ARPaymentDetailSlideinProps {
  paymentId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ARPaymentDetailSlidein({
  paymentId,
  isOpen,
  onOpenChange,
}: ARPaymentDetailSlideinProps) {
  const gt = useGT();
  const { showToast } = useToast();
  const { value: detailViewSize, set: setDetailViewSize } =
    useLocalStorageValue<number>('ar-payment-detail-view-size', {
      defaultValue: 50,
    });

  const { payment, isLoading } = useArPayment(paymentId);
  const setStatus = useSetArPaymentStatus();
  const [editOpen, setEditOpen] = useState(false);

  const editable = payment ? isManualOrigin(payment.originSystem) : false;

  async function handleSetStatus(status: 'active' | 'reversed' | 'voided') {
    if (!payment) return;
    try {
      await setStatus.mutateAsync({ id: payment.id, status });
      showToast({
        title: gt('Payment updated', {
          context: 'toast title, payment status changed',
        }),
        color: 'success',
        icon: 'check',
      });
    } catch (err) {
      showToast({
        title: gt('Update failed', {
          context: 'toast title, payment status change failed',
        }),
        message: err instanceof Error ? err.message : '',
        color: 'error',
        icon: 'warning',
      });
    }
  }

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContentResizable
        resizableProps={{
          defaultSize: detailViewSize,
          onResize: setDetailViewSize,
          className: 'flex flex-col',
        }}
      >
        <SheetHeader className="shrink-0 grow-0">
          {isLoading ? (
            <SheetTitle>
              <T context="AR payment slide-in loading title">Loading...</T>
            </SheetTitle>
          ) : payment ? (
            <div className="flex items-center gap-2">
              <SheetTitle>
                {payment.referenceNumber ??
                  gt('(No reference)', {
                    context: 'AR payment title when reference number missing',
                  })}
              </SheetTitle>
              {getStatusBadge(payment.status, gt)}
              {!editable && <OriginReadOnlyBadge />}
            </div>
          ) : (
            <SheetTitle>
              <T context="AR payment slide-in when payment id not found">
                Payment not found
              </T>
            </SheetTitle>
          )}
        </SheetHeader>

        <div className="flex-1 min-h-0 overflow-auto">
          {isLoading ? (
            <div className="p-4 text-sm text-muted-foreground">
              <T context="AR payment slide-in body loading">Loading...</T>
            </div>
          ) : payment ? (
            <ARPaymentDetails payment={payment} />
          ) : (
            <div className="p-4 text-sm text-muted-foreground">
              <T context="AR payment slide-in body when not found">
                Payment not found
              </T>
            </div>
          )}
        </div>

        <SheetFooter className="shrink-0 grow-0 flex items-center justify-between border-t">
          <div className="flex items-center gap-2">
            {payment && editable && (
              <>
                <Button variant="outline" onClick={() => setEditOpen(true)}>
                  <T context="edit AR payment">Edit</T>
                </Button>
                {payment.status === 'active' ? (
                  <>
                    <Button
                      variant="outline"
                      disabled={setStatus.isPending}
                      onClick={() => handleSetStatus('reversed')}
                    >
                      <T context="reverse AR payment">Reverse</T>
                    </Button>
                    <Button
                      variant="outline"
                      disabled={setStatus.isPending}
                      onClick={() => handleSetStatus('voided')}
                    >
                      <T context="void AR payment">Void</T>
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="outline"
                    disabled={setStatus.isPending}
                    onClick={() => handleSetStatus('active')}
                  >
                    <T context="reactivate AR payment">Reactivate</T>
                  </Button>
                )}
              </>
            )}
          </div>
          <Button onClick={() => onOpenChange(false)} className="w-fit">
            <T context="close AR payment detail slide-in">Close</T>
          </Button>
        </SheetFooter>
      </SheetContentResizable>

      {payment && editable && (
        <PaymentFormDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          payment={payment}
        />
      )}
    </Sheet>
  );
}
